/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm } from "react-hook-form";
import AppButton from "../buttons/AppButton";
import { FieldText, FieldPhone, FieldCalendar, FieldTextArea, FieldDropdown, FieldTextMultiple } from "./FormElements";
import { usePayAnInvoiceFormData } from "@/store/payAnInvoiceFormsStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { createDynamicSchemaSupportThirdTab } from "./schemaHelper";
import { Form } from "../ui/form";
import AppText from "../other/AppText";
import { InvoicesPayload } from "@/app/[lang]/pay-an-invoice/invoices";
import { fetchFromAPI } from "@/app/[lang]/pay-an-invoice/apiService";
import { API_CONFIG, ValidateUser } from "./constants";
import ReCAPTCHA from "react-google-recaptcha";
import { useLocale } from "@/store/clientStore";
import LoadingDots from "../other/LoadingDots";
import { validateCustomerName } from "./helpers";
import { createPortal } from "react-dom";
import { toast, Toaster } from "sonner";
import { CrossIcon, StatusIcon } from "@/assets/Icons";

interface Params {
  account_number: string;
  invoice_no: string;
  additional_data: boolean;
}

interface ValidateParams {
  email: string;
  firstName?: string;
  lastName?: string;
  accountNumber: string;
  invoiceNumber?: string;
  phone?: string;
}

const checkFormValues = (formValues: { [key: string]: string }) => {
  for (const item in formValues) {
    if (formValues[item] == "" || !formValues[item]) {
      return false;
    }
  }
  return true;
};

const getInvoice = (formValues: any) => {
  const invoiceObj: any = {};
  Object.keys(formValues).forEach((formValue) => {
    if (formValue.includes("PAI_Invoice")) {
      invoiceObj[formValue] = formValues[formValue];
    }
  });
  return invoiceObj;
};

const checkIdenticalInvoices = (value: number) => {
  const map = new Map();

  const invoiceArr = Object.values(value);

  const valuekdn = invoiceArr.some((item) => {
    if (map.has(item)) {
      return true;
    } else {
      map.set(item, 1);
    }
  });
  return valuekdn;
};

const getInvoiceNumber = (invoice: number) => {
  let result = "";
  Object.values(invoice).forEach((invoice, index) => {
    result += `${invoice},`;
  });
  return result;
};

const convertInvocieAsKeys = (invoices: { [key: string]: string }) => {
  const result: { [key: string]: string } = {};
  Object.entries(invoices).forEach(([key, value]: any) => {
    result[value] = key;
  });
  return result;
};

const checkIncorrectInvocies = (invoicesObjs: any, invoices: { [key: string]: string }) => {
  const incorrectInvoice: string[] = [];
  const invoiceNumbers: number[] = [];
  if (!invoicesObjs) {
    return Object.keys(invoices);
  }
  invoicesObjs.forEach((invoice: { DOC_NO: number }) => {
    invoiceNumbers.push(invoice.DOC_NO);
  });

  Object.keys(invoices).forEach((key) => {
    if (invoiceNumbers.indexOf(Number(key)) < 0) {
      incorrectInvoice.push(key);
    }
  });

  return incorrectInvoice;
};

const getNegativeInvoice = (invoicesObjs: any, invoices: { [key: string]: string }) => {
  const negativeInvoice: string[] = [];
  const invoiceNumbers: number[] = [];
  if (!invoicesObjs) {
    return Object.keys(invoices);
  }
  invoicesObjs.forEach((invoice: any) => {
    if (invoice?.BALANCE < 0) {
      invoiceNumbers.push(invoice?.DOC_NO);
    }
  });

  Object.keys(invoices).forEach((key) => {
    if (invoiceNumbers.indexOf(Number(key)) > -1) {
      negativeInvoice.push(key);
    }
  });

  return negativeInvoice;
};

const getInvalidOrZeroInvoices = (invoicesObjs: any, invoices: { [key: string]: string }) => {
  const zeroInvoice: string[] = [];
  const invoiceNumbers: number[] = [];
  if (!invoicesObjs) {
    return Object.keys(invoices);
  }
  invoicesObjs.forEach((invoice: any) => {
    invoiceNumbers.push(invoice?.DOC_NO);
  });

  Object.keys(invoices).forEach((key) => {
    if (invoiceNumbers.indexOf(Number(key)) < 0) {
      zeroInvoice.push(key);
    }
  });

  return zeroInvoice;
};

const setErrorForIncorrectInvoices = (
  incorrectInvoices: string[],
  form: any,
  invoiceAsKeys: { [key: string]: string },
  error: string
) => {
  incorrectInvoices.forEach((invoice: string) => {
    form.setError(invoiceAsKeys[invoice], { message: error });
  });
};

const removeNegativeAndZeroValues = (invoiceResponse: any) => {
  const values = invoiceResponse?.STATEMENT_LINES?.item.filter((item: any) => {
    if (item?.BALANCE > 0) {
      return true;
    }
    return false;
  });
  if (invoiceResponse?.STATEMENT_LINES?.item) {
    invoiceResponse.STATEMENT_LINES.item = values;
  }

  return invoiceResponse;
};
const lengthExceptZeroAndNegative = (invoiceResponse: any) => {
  let count = 0;
  invoiceResponse?.STATEMENT_LINES?.item.forEach((ele: { BALANCE: number }) => {
    if (ele?.BALANCE > 0) {
      count++;
    }
  });
  return count;
};

const FormRequestPayAnInvoiceStep1 = ({ formData }: { formData: any }) => {
  const formSchema = z.object({});
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [dynamicFormSchema, setDynamicFormSchema] = useState<z.ZodSchema>(formSchema);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [nextDisable, setNextDisable] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isEmailExists, setIsEmailExists] = useState<boolean>(false);

  const setStepAndFormData = usePayAnInvoiceFormData((state) => state.setStepAndFormData);
  const formDataStore = usePayAnInvoiceFormData((state) => state.formData);
  const setInvoiceData = usePayAnInvoiceFormData((state) => state.setCustomerInvoiceResponse);
  const enteredEmail = usePayAnInvoiceFormData((state) => state.enteredEmail);
  const setEnteredEmail = usePayAnInvoiceFormData((state) => state.setEnteredEmail);
  const guestType = usePayAnInvoiceFormData((state) => state.guestType);
  const adddedInvocie = usePayAnInvoiceFormData((state) => state.addedInvoice);
  const setAddedInvoice = usePayAnInvoiceFormData((state) => state.setAddedInvoice);
  const setShowMsgForMissingInvoices = usePayAnInvoiceFormData((state) => state.setShowMsgForMissingInvoices);
  const [textFields, setTextFields] = useState<any>(adddedInvocie);
  const { setGuestType, setUserType } = usePayAnInvoiceFormData();
  const { locale } = useLocale();

  const [isLoading, setIsLoading] = useState(false);

  const primaryButton = formData?.groups?.[1];
  const secondaryButton = formData?.groups?.[2];

  const formDefaultValues = useMemo(() => {
    const res: any = {};

    (formData.groups[0].rows || []).forEach((row: any) => {
      (row.fields || []).forEach((field: any) => {
        res[field.name] = formDataStore[field.name] || "";
      });
    });

    if (textFields.length > 0) {
      textFields.forEach((item: any) => {
        res[item.name] = formDataStore[item.name] || "";
      });
    }
    return res;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const form = useForm<z.infer<typeof dynamicFormSchema>>({
    resolver: zodResolver(dynamicFormSchema),
    defaultValues: formDefaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const createDynamicSchema = () => {
      const zObj = createDynamicSchemaSupportThirdTab(formData.groups, textFields);
      const formSchema = z.object(zObj);
      setDynamicFormSchema(formSchema);
    };

    createDynamicSchema();
  }, [formData, textFields]);

  async function onSubmit(values: z.infer<typeof dynamicFormSchema>) {
    setShowMsgForMissingInvoices(false);
    if (!recaptchaToken) {
      form.setError("recaptcha", { message: "Please complete the reCAPTCHA" });
      return;
    }

    const { firstNameError, lastNameError } = validateCustomerName(formData, form, values);

    const invoices = getInvoice(form.getValues());
    const IdenticalVlaue = checkIdenticalInvoices(invoices);

    if (IdenticalVlaue) {
      toast.error("Identical invoices found");
    } else {
      if (!firstNameError && !lastNameError) {
        setIsLoading(true);
        handleNavigateToInvoice();
      }
    }
  }

  useEffect(() => {
    if (checkFormValues(form.getValues()) && recaptchaToken) {
      setNextDisable(false);
    } else {
      setNextDisable(true);
    }
  }, [form, recaptchaToken]);

  const handleEmailValidation = async () => {
    const email = form.getValues().PAI_Email;
    const accountNumber = form.getValues().PAI_Account;
    if (enteredEmail != email || guestType == 0) {
      const validateParams: ValidateParams = {
        email: email,
        accountNumber: accountNumber,
      };
      const headers = {
        guestpaymentkey: `${process.env.NEXT_PUBLIC_API_KEY}`,
      };

      const validateRespones = await fetchFromAPI<any>({
        method: "post",
        endpoint: API_CONFIG.ENDPOINTS.validateUser,
        body: validateParams,
        headers: headers,
        returnSpecificErrorMessage: true,
      });

      if (validateRespones.Status == ValidateUser.success) {
        setIsEmailExists(true);
        setUserType(false);
      } else {
        setIsEmailExists(false);
        setUserType(true);
      }
      setEnteredEmail(email);
      setIsLoading(false);
      setShowModal(true);
      setRecaptchaToken(null);
    }
  };

  const handleNavigateToInvoice = async () => {
    const invoices = getInvoice(form.getValues());
    const invoiceAsKeys = convertInvocieAsKeys(invoices);
    const values = form.getValues();
    const params: Params = {
      account_number: values.PAI_Account,
      invoice_no: getInvoiceNumber(invoices), //comma seperator
      additional_data: true,
    };
    const headers = {
      guestpaymentkey: `${process.env.NEXT_PUBLIC_API_KEY}`,
    };

    const invoiceResponse = await fetchFromAPI<InvoicesPayload>({
      method: "get",
      endpoint: API_CONFIG.ENDPOINTS.invoices,
      queryParameters: params,
      headers: headers,
    });
    const invoiceErr = formData?.groups?.[0]?.rows?.[2]?.fields.filter((item: { name: string }) => {
      if (item.name === "PAI_Invoice") {
        return item;
      }
    });

    const accountErr = formData?.groups?.[0]?.rows?.[2]?.fields.filter((item: { name: string }) => {
      if (item.name === "PAI_Account") {
        return item;
      }
    });

    if (invoiceResponse.RETURN != "") {
      const canadaResponse = await fetchFromAPI<InvoicesPayload>({
        method: "get",
        endpoint: API_CONFIG.ENDPOINTS.invoices,
        queryParameters: { ...params, company_code: 1100 },
        headers: headers,
      });

      if (canadaResponse.RETURN != "") {
        setIsLoading(false);
        form.setError("PAI_Invoice", { message: invoiceErr[0].invalidError });
        form.setError("PAI_Account", { message: accountErr[0].invalidError });
        const incorrectInvoices = checkIncorrectInvocies(canadaResponse?.STATEMENT_LINES?.item, invoiceAsKeys);
        setErrorForIncorrectInvoices(incorrectInvoices, form, invoiceAsKeys, invoiceErr[0].invalidError);
        setRecaptchaToken(null);
        setNextDisable(true);
      } else {
        setIsLoading(false);
        setAddedInvoice(textFields);
        if (canadaResponse?.STATEMENT_LINES?.item.length != Object.keys(invoiceAsKeys).length) {
          const incorrectInvoices = checkIncorrectInvocies(canadaResponse?.STATEMENT_LINES?.item, invoiceAsKeys);
          setErrorForIncorrectInvoices(incorrectInvoices, form, invoiceAsKeys, invoiceErr?.[0]?.invalidError);
          setNextDisable(true);
        } else if (removeNegativeAndZeroValues(canadaResponse)?.STATEMENT_LINES?.item.length == 0) {
          setErrorForIncorrectInvoices(Object.keys(invoiceAsKeys), form, invoiceAsKeys, invoiceErr?.[0]?.invalidError);
          setNextDisable(true);
        } else {
          const email = form.getValues().PAI_Email;

          if (enteredEmail != email || guestType == 0) {
            setNextDisable(true);
            handleEmailValidation();
          } else {
            setStepAndFormData(2, form.getValues());
          }
        }
        setInvoiceData(removeNegativeAndZeroValues(canadaResponse));
        if (
          canadaResponse?.STATEMENT_LINES?.item.length !=
          removeNegativeAndZeroValues(canadaResponse)?.STATEMENT_LINES?.item.length
        ) {
          setShowMsgForMissingInvoices(true);
        }
      }
    } else {
      setIsLoading(false);
      setAddedInvoice(textFields);
      if (
        getNegativeInvoice(invoiceResponse?.STATEMENT_LINES?.item, invoiceAsKeys).length ===
        Object.keys(invoiceAsKeys).length
      ) {
        setErrorForIncorrectInvoices(
          getNegativeInvoice(invoiceResponse?.STATEMENT_LINES?.item, invoiceAsKeys),
          form,
          invoiceAsKeys,
          invoiceErr[0]?.errorMessage
        );
        setNextDisable(true);
      } else if (lengthExceptZeroAndNegative(invoiceResponse) == 0) {
        setErrorForIncorrectInvoices(
          getInvalidOrZeroInvoices(invoiceResponse?.STATEMENT_LINES?.item, invoiceAsKeys),
          form,
          invoiceAsKeys,
          invoiceErr?.[0]?.invalidError
        );
        setErrorForIncorrectInvoices(
          getNegativeInvoice(invoiceResponse?.STATEMENT_LINES?.item, invoiceAsKeys),
          form,
          invoiceAsKeys,
          formData?.errorMessage
        );
        setNextDisable(true);
      } else if (invoiceResponse?.STATEMENT_LINES?.item.length != Object.keys(invoiceAsKeys).length) {
        const incorrectInvoices = checkIncorrectInvocies(invoiceResponse?.STATEMENT_LINES?.item, invoiceAsKeys);
        setErrorForIncorrectInvoices(incorrectInvoices, form, invoiceAsKeys, invoiceErr[0]?.invalidError);
        setNextDisable(true);
      } else {
        const email = form.getValues().PAI_Email;

        if (enteredEmail != email || guestType == 0) {
          setNextDisable(true);
          handleEmailValidation();
        } else {
          setStepAndFormData(2, form.getValues());
        }
      }
      setInvoiceData(removeNegativeAndZeroValues(invoiceResponse));

      if (
        Object.keys(invoiceAsKeys).length != removeNegativeAndZeroValues(invoiceResponse)?.STATEMENT_LINES?.item.length
      ) {
        setShowMsgForMissingInvoices(true);
      }
    }
  };

  const handleNextButton = () => {
    const btn = document.getElementById("targetSubmit");

    btn && btn.click();
  };

  const handleRecaptchaChange = (token: string | null) => {
    if (token) {
      if (checkFormValues(form.getValues())) {
        setNextDisable(false);
      } else {
        setNextDisable(true);
      }
    } else {
      setNextDisable(true);
    }
    setRecaptchaToken(token);
  };

  const handleRemoveInvoice = (name: any, value: any) => {
    form.unregister(name);
    const arr = textFields.filter((item: any) => item.name != name);
    setTextFields(arr);

    arr.forEach((item: any) => {
      form.setValue(item.name, form.getValues(item.name));
    });
  };

  useEffect(() => {
    const checkRecaptcha = setInterval(() => {
      if (window.grecaptcha) {
        setIsRecaptchaReady(true);
        clearInterval(checkRecaptcha);
      }
    }, 500);
    return () => clearInterval(checkRecaptcha);
  }, []);

  let globalLoader: any;
  if (typeof document !== "undefined") {
    globalLoader = document.getElementById("globalLoader");
  }

  return (
    <>
      <div className="lg:col-span-10 lg:col-start-2 lg:px-10 col-span-4 px-6 pt-12 bg-white">
        <AppText type="SUB_HEADING_MEDIUM" className="mb-[28px]">
          {formData.groups[0].heading}
        </AppText>

        {showModal &&
          createPortal(
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)", // Optional semi-transparent overlay
                zIndex: 1000, // Ensure it's on top
                pointerEvents: showModal ? "all" : "none",
                display: showModal ? "block" : "none",
              }}
            >
              <div className="flex items-center justify-center w-full h-full">
                <div className="w-[600px]">
                  <div className="flex flex-col justify-center p-8 bg-white rounded-md">
                    <div className="flex justify-end">
                      <div
                        onClick={() => {
                          setGuestType(0);
                          setShowModal(false);
                        }}
                        className="cursor-pointer"
                      >
                        <AppText type={"BODY_MEDIUM"}>X</AppText>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <AppText type={"HEADLINE_MEDIUM"}>{primaryButton?.heading}</AppText>
                    </div>
                    <div className="flex justify-center m-2">
                      <AppText type={"BODY_MEDIUM"} className="text-center">
                        {!isEmailExists ? primaryButton?.bannerDescription : secondaryButton?.bannerDescription}
                      </AppText>
                    </div>

                    <div className="flex justify-center m-2 border-b-[1px] border-[#E9EAEB]"></div>
                    <div className=" flex flex-row flex-wrap justify-around w-full mt-2 mb-2">
                      <AppButton
                        intent="primary"
                        label={primaryButton?.primaryCtaLabel}
                        type="submit"
                        className={`flex-1 px-10 min-w-[250px] mt-2 hover:bg-${primaryButton?.primaryOnHover} enabled:active:bg-${primaryButton?.primaryBackground}`}
                        onMouseDown={() => {
                          setShowModal(false);
                          setIsLoading(true);
                          //handleNavigateToInvoice();
                          setStepAndFormData(2, form.getValues());
                          setGuestType(1);
                        }}
                      />

                      <AppButton
                        intent="primary"
                        label={!isEmailExists ? primaryButton?.secondaryCtaLabel : secondaryButton?.secondaryCtaLabel}
                        type="submit"
                        className={`flex-1 px-10 min-w-[250px] mt-2 hover:bg-${secondaryButton?.secondaryOnHover} bg-${secondaryButton?.secondaryBackground} text-black`}
                        onMouseDown={() => {
                          setGuestType(0);
                          if (!isEmailExists) {
                            primaryButton?.secondaryCtaLink && (document.location = primaryButton?.secondaryCtaLink);
                          } else {
                            secondaryButton?.secondaryCtaLink &&
                              (document.location = secondaryButton?.secondaryCtaLink);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            globalLoader
          )}

        {isLoading ? (
          <div className="flex items-center justify-center w-full h-full mb-12">
            <LoadingDots />
          </div>
        ) : (
          <Form {...form}>
            <form
              className="flex flex-col bg-white gap-[28px]"
              autoComplete="off"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              {(formData.groups[0].rows || []).map((row: any) => (
                <div key={row.id} className="lg:flex-row flex flex-col gap-6">
                  {row.fields?.length &&
                    row.fields.map((field: any) => {
                      if (field.fieldType === "fieldText" && field.name === "PAI_Invoice") {
                        return (
                          <FieldTextMultiple
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            placeholder={field.placeholder}
                            loading={false}
                            label={field.label}
                            type={field.name === "PAI_Invoice" || field.name === "PAI_Account" ? "number" : ""}
                            textFields={textFields}
                            setTextFields={setTextFields}
                            handleRemoveInvoice={handleRemoveInvoice}
                            AddInvoiceLabel={field.buttonLabel}
                            footnote={field.footnote}
                          />
                        );
                      } else if (field.fieldType === "fieldText") {
                        return (
                          <FieldText
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            placeholder={field.placeholder}
                            loading={false}
                            label={field.label}
                            type={field.name === "PAI_Invoice" || field.name === "PAI_Account" ? "number" : ""}
                          />
                        );
                      } else if (field.fieldType === "fieldPhone") {
                        return (
                          <FieldPhone
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            loading={false}
                            placeholder={field.placeholder || "Enter your phone number..."}
                            label={field.label}
                          />
                        );
                      } else if (field.fieldType === "fieldCalendar") {
                        return (
                          <FieldCalendar
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            placeholder={field.placeholder}
                            loading={false}
                            label={field.label}
                          />
                        );
                      } else if (field.fieldType === "fieldTextArea") {
                        return (
                          <FieldTextArea
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            loading={false}
                            placeholder={field.placeholder}
                          />
                        );
                      } else if (field.fieldType === "fieldDropdown") {
                        return (
                          <FieldDropdown
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            placeholder={field.placeholder}
                            label={field.label}
                            options={field.options}
                          />
                        );
                      } else if (field.fieldType === "fieldEmail") {
                        return (
                          <FieldText
                            key={field.id}
                            control={form.control}
                            name={field.name}
                            placeholder={field.placeholder}
                            loading={false}
                            label={field.label}
                          />
                        );
                      }
                      return null;
                    })}
                </div>
              ))}
              <div className="mt-12 mb-[78px]">
                <button id="targetSubmit" className="hidden" aria-label="submit target" type="submit"></button>
                <div className="flex flex-row flex-wrap justify-end gap-4 bg-white">
                  {isRecaptchaReady ? (
                    <ReCAPTCHA
                      key={locale}
                      sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY_V2 ?? "default_site_key"}
                      onChange={handleRecaptchaChange}
                      hl={locale}
                    />
                  ) : (
                    <p>Loading ReCAPTCHA...</p>
                  )}
                  <AppButton
                    intent="primary"
                    label={formData.ctaNext}
                    type="submit"
                    className="px-10"
                    onMouseDown={handleNextButton}
                    disabled={nextDisable}
                  />
                </div>
              </div>
            </form>
          </Form>
        )}
      </div>
    </>
  );
};
export default FormRequestPayAnInvoiceStep1;
